import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';

const CreateForm: React.FC = () => {
    const [templates, setTemplates] = useState<any[]>([]);
    const [clients, setClients] = useState<any[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
    const [selectedClientUserId, setSelectedClientUserId] = useState<number | null>(null);
    const [formName, setFormName] = useState('');
    const [formId, setFormId] = useState<number | null>(null);
    const [clientLinkGuid, setClientLinkGuid] = useState<string | null>(null);
    const [emailSent, setEmailSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isClientsLoaded, setIsClientsLoaded] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const loadData = async () => {
            await fetchTemplates();
            await fetchClients();
            setIsClientsLoaded(true);
        };
        loadData();
    }, []);

    useEffect(() => {
        if (!isClientsLoaded || !selectedClientId) {
            setSelectedClientUserId(null);
            return;
        }

        const selectedClient = clients.find(client => client.id === selectedClientId);
        if (selectedClient && selectedClient.clientUserId) { // Updated to clientUserId
            setSelectedClientUserId(selectedClient.clientUserId);
        } else {
            setSelectedClientUserId(null);
        }
    }, [selectedClientId, clients, isClientsLoaded]);

    const fetchTemplates = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Templates`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTemplates(response.data);
        } catch (error) {
            console.error('Failed to fetch templates:', error);
            setError('Failed to load templates. Please try again.');
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Clients`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setClients(response.data);
        } catch (error) {
            console.error('Failed to fetch clients:', error);
            setError('Failed to load clients. Please try again.');
        }
    };

    const createForm = async () => {
        if (!selectedTemplateId) {
            setError('Please select a template.');
            return;
        }
        if (!formName.trim()) {
            setError('Please enter a form name.');
            return;
        }
        if (selectedClientId && !selectedClientUserId) {
            setError('Selected client has no associated user account. Please create one first.');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const payload = { 
                templateId: selectedTemplateId, 
                name: formName,
                clientId: selectedClientId,
                clientUserId: selectedClientUserId
            };
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/Forms`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const { id } = response.data; // Adjusted to match actual response
            setFormId(id);
            setClientLinkGuid(null); // Removed clientLinkGuid as it’s not in the response
            setEmailSent(true);
            setFormName('');
            setSelectedTemplateId(null);
            setSelectedClientId(null);
            setSelectedClientUserId(null);
            alert('Form created successfully!');
        } catch (error) {
            console.error('Failed to create form:', error);
            setError('Failed to create form. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container mt-5">
            <h2 className="mb-4 text-center">Create Form</h2>
            <div className="card shadow-sm mb-4">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Select a Template</h4>
                    <Link to="/templates" className="btn btn-light btn-sm">
                        New Template
                    </Link>
                </div>
                <div className="card-body">
                    {error && <div className="alert alert-danger">{error}</div>}
                    <div className="mb-3">
                        <label className="form-label">Form Name</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter form name (e.g., Smith Wedding)"
                            value={formName}
                            onChange={(e) => setFormName(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Client (Optional)</label>
                        <select
                            className="form-control"
                            value={selectedClientId || ''}
                            onChange={(e) => setSelectedClientId(e.target.value ? parseInt(e.target.value) : null)}
                            disabled={isLoading}
                        >
                            <option value="">No client</option>
                            {clients.map(client => (
                                <option key={client.id} value={client.id}>{client.name}</option>
                            ))}
                        </select>
                    </div>
                    {templates.length === 0 ? (
                        <div className="alert alert-warning text-center">
                            No templates found. Create a new template to get started.
                        </div>
                    ) : (
                        <ul className="list-group list-group-flush">
                            {templates.map(template => (
                                <li
                                    key={template.id}
                                    className="list-group-item d-flex justify-content-between align-items-center"
                                >
                                    <span>{template.name}</span>
                                    <button
                                        onClick={() => setSelectedTemplateId(template.id)}
                                        className={`btn btn-sm ${selectedTemplateId === template.id ? 'btn-primary' : 'btn-outline-primary'}`}
                                        disabled={isLoading}
                                    >
                                        {selectedTemplateId === template.id ? 'Selected' : 'Select'}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="text-center mt-4">
                        <button
                            onClick={createForm}
                            className="btn btn-primary"
                            disabled={isLoading || !selectedTemplateId}
                        >
                            {isLoading ? 'Creating...' : 'Create Form'}
                        </button>
                    </div>
                    {formId && (
                        <div className="mt-4">
                            <div className="alert alert-success text-center">
                                <p className="mb-1">Form created! View details here:</p>
                                <Link to={`/form-details/${formId}`} className="btn btn-outline-primary btn-sm">
                                    Form ID: {formId}
                                </Link>
                            </div>
                        </div>
                    )}
                    {emailSent && (
                        <div className="alert alert-info text-center mt-3">
                            Email notification sent to client (mock).
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateForm;